import React from 'react';
import { AlertCircleIcon, ScissorsIcon, SlidersIcon, ZapIcon, CogIcon } from 'lucide-react';
export function Algorithm() {
  const steps = [{
    number: '01',
    icon: AlertCircleIcon,
    title: 'CHALLENGE EVERYTHING',
    description: "Before we write a single line of code, we question the requirement. Is this a real problem or a phantom problem? Does solving it actually move profit? If we can't articulate the business outcome, we don't build it.",
    example: 'Client asks for a dashboard. We ask: "What decision will this dashboard help you make?" Often, the answer reveals they don\'t need a dashboard at all.',
    color: 'amber'
  }, {
    number: '02',
    icon: ScissorsIcon,
    title: 'RUTHLESS DELETION',
    description: "Every step in your process is a tax on profit. We delete steps that don't add value. If we don't restore at least 10% of your team's time, we weren't bold enough. Most \"automation\" just speeds up waste.",
    example: 'A 12-step approval process becomes 3 steps. Not by automating all 12, but by deleting 9 that existed only because "we\'ve always done it that way."',
    color: 'green'
  }, {
    number: '03',
    icon: SlidersIcon,
    title: 'SIMPLIFY',
    description: 'What survives deletion gets simplified. We remove complexity, not add it. Every integration point is a future maintenance burden. Every custom feature is technical debt. Simple systems scale. Complex ones break.',
    example: 'Instead of integrating 5 tools, we consolidate to 2 and use native features. Less to maintain, less to break, easier to understand.',
    color: 'amber'
  }, {
    number: '04',
    icon: ZapIcon,
    title: 'ACCELERATE',
    description: 'We ship in days, not weeks. Every delay is profit lost. We break work into atomic tasks and set deadlines based on what\'s physically possible—not "how we\'ve always done it." Fast feedback loops mean fast course correction.',
    example: 'Week 1: working prototype. Week 2: real users testing. Week 3: measuring results. Compare that to 6-month projects that launch to crickets.',
    color: 'green'
  }, {
    number: '05',
    icon: CogIcon,
    title: 'AUTOMATE LAST',
    description: "Only after we've challenged, deleted, simplified, and accelerated do we automate. Automating a broken process just gives you broken results faster. Build it right first. Then make it fast.",
    example: 'The automation code is often the smallest part of the project. Most of the value came from steps 1-4. The automation just locks in the gains.',
    color: 'amber'
  }];
  return <div className="w-full bg-zinc-950 py-24 relative overflow-hidden" data-id="element-60">
      <div className="absolute inset-0 opacity-5 animate-grid-move" style={{
      backgroundImage: 'radial-gradient(circle at 50% 50%, #ffa500 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }} data-id="element-61" />
      {/* Floating particles */}
      {[...Array(15)].map((_, i) => <div key={i} className="absolute w-1 h-1 bg-amber-500 rounded-full animate-float opacity-30" style={{
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 5}s`,
      animationDuration: `${8 + Math.random() * 12}s`
    }} data-id="element-62" />)}
      <div className="container mx-auto px-6 relative z-10" data-id="element-63">
        <div className="max-w-5xl mx-auto" data-id="element-64">
          {/* Header */}
          <div className="mb-16 animate-fade-in-up" data-id="element-65">
            <div className="inline-block border-4 border-amber-500 bg-black px-6 py-3 mb-6 animate-border-pulse shadow-[0_0_30px_rgba(255,165,0,0.4)]" data-id="element-66">
              <h2 className="text-4xl md:text-5xl font-bold text-amber-500 font-mono" data-id="element-67">
                THE ALGORITHM
              </h2>
            </div>
            <p className="text-green-400 text-xl font-mono mb-4 animate-fade-in-up" style={{
            animationDelay: '0.2s'
          }} data-id="element-68">
              // FIVE STEPS. EVERY TIME. NO SHORTCUTS.
            </p>
            <p className="text-amber-100 text-lg leading-relaxed animate-fade-in-up" style={{
            animationDelay: '0.4s'
          }} data-id="element-69">
              This isn't theory. It's the exact process we follow on every
              engagement. Skip one step and you're building on sand. Follow all
              five and you get systems that actually drive profit.
            </p>
          </div>
          {/* Steps */}
          <div className="space-y-8" data-id="element-70">
            {steps.map((step, index) => <div key={index} className={`border-4 ${step.color === 'amber' ? 'border-amber-600' : 'border-green-500'} bg-black p-8 shadow-[0_0_40px_rgba(${step.color === 'amber' ? '255,165,0' : '0,255,0'},0.2)] hover:shadow-[0_0_60px_rgba(${step.color === 'amber' ? '255,165,0' : '0,255,0'},0.5)] transition-all duration-500 hover:scale-[1.02] animate-fade-in-up group relative overflow-hidden`} style={{
            animationDelay: `${0.6 + index * 0.1}s`
          }} data-id="element-71">
                {/* Animated corner accents */}
                <div className={`absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 ${step.color === 'amber' ? 'border-amber-400' : 'border-green-400'} animate-pulse`} data-id="element-72" />
                <div className={`absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 ${step.color === 'amber' ? 'border-amber-400' : 'border-green-400'} animate-pulse`} style={{
              animationDelay: '0.5s'
            }} data-id="element-73" />
                <div className="flex flex-col md:flex-row gap-6" data-id="element-74">
                  {/* Number and icon */}
                  <div className="flex-shrink-0" data-id="element-75">
                    <div className={`border-2 ${step.color === 'amber' ? 'border-amber-500 bg-amber-950' : 'border-green-500 bg-green-950'} w-20 h-20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500 relative overflow-hidden`} data-id="element-76">
                      <div className={`absolute inset-0 ${step.color === 'amber' ? 'bg-amber-500' : 'bg-green-500'} opacity-0 group-hover:opacity-20 transition-opacity duration-500`} data-id="element-77" />
                      <step.icon className={`w-10 h-10 ${step.color === 'amber' ? 'text-amber-500' : 'text-green-400'} animate-pulse relative z-10 group-hover:rotate-12 transition-transform duration-500`} data-id="element-78" />
                    </div>
                    <div className={`text-4xl font-bold ${step.color === 'amber' ? 'text-amber-500' : 'text-green-400'} font-mono opacity-50 group-hover:opacity-100 transition-opacity duration-500`} data-id="element-79">
                      {step.number}
                    </div>
                  </div>
                  {/* Content */}
                  <div className="flex-grow" data-id="element-80">
                    <h3 className={`text-2xl font-bold mb-4 font-mono ${step.color === 'amber' ? 'text-amber-500' : 'text-green-400'} group-hover:text-glow transition-all duration-300`} data-id="element-81">
                      {step.title}
                    </h3>
                    <p className="text-amber-100 leading-relaxed mb-6" data-id="element-82">
                      {step.description}
                    </p>
                    <div className={`border-l-4 ${step.color === 'amber' ? 'border-amber-500' : 'border-green-500'} pl-4 py-2 bg-zinc-900 relative overflow-hidden group-hover:bg-zinc-800 transition-colors duration-300`} data-id="element-83">
                      <div className={`absolute left-0 top-0 bottom-0 w-1 ${step.color === 'amber' ? 'bg-amber-400' : 'bg-green-400'} transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500`} data-id="element-84" />
                      <p className={`font-mono text-sm mb-2 ${step.color === 'amber' ? 'text-amber-400' : 'text-green-300'}`} data-id="element-85">
                        {'>'} EXAMPLE:
                      </p>
                      <p className="text-amber-100 text-sm italic" data-id="element-86">
                        {step.example}
                      </p>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
          {/* Bottom note */}
          <div className="mt-12 border-4 border-amber-600 bg-black p-6 animate-border-pulse animate-fade-in-up relative overflow-hidden group" style={{
          animationDelay: '1.1s'
        }} data-id="element-87">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-950/0 via-amber-950/20 to-amber-950/0 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" data-id="element-88" />
            <p className="text-amber-100 leading-relaxed relative z-10" data-id="element-89">
              <span className="text-amber-500 font-semibold" data-id="element-90">
                The uncomfortable truth:
              </span>{' '}
              Most of the value comes from steps 1-3. Challenging requirements,
              deleting waste, and simplifying processes often delivers more ROI
              than the automation itself. But nobody wants to hear that—they
              want to buy software, not rethink their processes. That's why
              we're partners, not vendors.
            </p>
          </div>
        </div>
      </div>
      <style data-id="element-91">{`
        @keyframes border-pulse {
          0%, 100% {
            box-shadow: 0 0 20px rgba(255, 165, 0, 0.3);
          }
          50% {
            box-shadow: 0 0 40px rgba(255, 165, 0, 0.6);
          }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-100px) translateX(50px);
          }
        }
        @keyframes grid-move {
          0% {
            transform: translate(0, 0);
          }
          100% {
            transform: translate(40px, 40px);
          }
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-border-pulse {
          animation: border-pulse 3s ease-in-out infinite;
        }
        .animate-float {
          animation: float linear infinite;
        }
        .animate-grid-move {
          animation: grid-move 20s linear infinite;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
        }
        .text-glow {
          text-shadow: 0 0 20px currentColor;
        }
      `}</style>
    </div>;
}